import { Icon } from '@iconify/react';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useCallback, useState } from 'react';
import { canAccess, MODULES } from '../../../auth/permissions';
import { useAuth } from '../../auth/auth.context';
import { getJobFloorsForJob, JobFloor } from '../../admin/admin.api';
import { ModalWrapper, SectionTitle } from '../../shared/components';
import useAsyncData from '../../shared/hooks/useAsyncData';
import AssignWorkModal from './AssignWorkModal';
import JobStepDialog, { JobStepKey } from './JobStepDialog';
import { WorkJobReportRow } from './workJobReport.api';

interface WorkJobActionsModalProps {
  /** The job the actions apply to; `null` keeps the dialog closed. */
  row: WorkJobReportRow | null;
  onClose: () => void;
  /** Called after any of the panels saves, so the report can refetch. */
  onSaved?: () => void;
}

/**
 * The other things that can be done to a job from its report row, gathered behind one
 * button: the Work Details and Quotation panels, and the Assign Work screen — which now
 * opens here rather than from the main menu, so it always arrives with a job in hand.
 *
 * Each opens over this dialog; this one stays behind them so closing a panel comes back
 * to the list of actions rather than to the report.
 *
 * The three run in order: Work Details describes the building, the Quotation prices it and
 * Assign Work cuts tasks from it, so each stays disabled until the one before it is done.
 *
 * Remounted per job (`key`) so one job's floors are never read as another's.
 */
export default function WorkJobActionsModal({ row, onClose, onSaved }: WorkJobActionsModalProps) {
  if (!row) return null;
  return <JobActions key={row.id} row={row} onClose={onClose} onSaved={onSaved} />;
}

function JobActions({
  row,
  onClose,
  onSaved,
}: {
  row: WorkJobReportRow;
  onClose: () => void;
  onSaved?: () => void;
}) {
  const { role } = useAuth();
  const [openStep, setOpenStep] = useState<JobStepKey | null>(null);
  const [assigning, setAssigning] = useState(false);

  /**
   * There is nothing to assign against a job whose work details have not been recorded —
   * the floor set is what the drawing and design tasks are cut from — and nothing to price
   * either, so both Quotation and Assign Work stay disabled until the Work Details panel
   * has saved at least one floor.
   */
  const fetchFloors = useCallback(() => getJobFloorsForJob(row.id), [row.id]);
  const { data: floors, loading: floorsLoading, refetch: refetchFloors } = useAsyncData<JobFloor[]>(
    fetchFloors,
    [],
    {
      code: 'JOB_FLOORS_CHECK_FAILED',
      errorMessage: 'Failed to check the job’s work details',
      toastOnError: false,
    }
  );
  const hasTypeOfWork = floors.length > 0;

  /**
   * Work Details itself needs the job classified first: without a construction work type the
   * panel has no work category to branch on and every section of it stays empty. That is set
   * on the Edit Job screen, so the button is dead until it has been.
   */
  const hasWorkType = Boolean(row.construction_work_type);

  // Recording the work details is what unlocks quoting and assigning, so re-read after a save.
  const handleStepSaved = () => {
    refetchFloors();
    onSaved?.();
  };

  /** Shown under whichever button is dead, so the reason is on screen rather than guessed at. */
  const blockedReason = (() => {
    if (!hasWorkType) return 'Set this job’s type of work on the Edit Job screen first.';
    if (floorsLoading) return null;
    if (!hasTypeOfWork) return 'Record the work details for this job before quoting or assigning work on it.';
    return null;
  })();

  return (
    <ModalWrapper open onClose={onClose} maxWidth='sm'>
      <SectionTitle
        title={`JOB ACTIONS · ${row.job_code}`}
        meta={
          <Typography variant='body2' color='text.secondary'>
            {row.job_name}
          </Typography>
        }
      />

      <Stack gap={1.5}>
        <Button
          variant='outlined'
          size='large'
          startIcon={<Icon icon='mdi:floor-plan' width={20} />}
          onClick={() => setOpenStep('typeOfWork')}
          disabled={!hasWorkType}
        >
          Work Details
        </Button>
        <Button
          variant='outlined'
          size='large'
          startIcon={<Icon icon='mdi:file-document-outline' width={20} />}
          onClick={() => setOpenStep('quotation')}
          disabled={floorsLoading || !hasTypeOfWork}
        >
          Quotation
        </Button>
        {/* Assigning is the analyst's job, so it is gated exactly as the old menu entry was. */}
        {canAccess(role, MODULES.ASSIGN_WORK) && (
          <Button
            variant='outlined'
            size='large'
            startIcon={<Icon icon='mdi:account-switch-outline' width={20} />}
            onClick={() => setAssigning(true)}
            disabled={floorsLoading || !hasTypeOfWork}
          >
            ASSIGN WORK
          </Button>
        )}

        {blockedReason && (
          <Typography variant='caption' color='text.secondary' textAlign='center'>
            {blockedReason}
          </Typography>
        )}
      </Stack>

      <Stack direction='row' justifyContent='flex-end'>
        <Button onClick={onClose}>Close</Button>
      </Stack>

      <JobStepDialog
        step={openStep}
        row={row}
        onClose={() => setOpenStep(null)}
        onSaved={handleStepSaved}
      />
      <AssignWorkModal
        row={assigning ? row : null}
        onClose={() => setAssigning(false)}
        onAssigned={onSaved}
      />
    </ModalWrapper>
  );
}
